import { useContext, type JSX } from 'react';
import { ButtonBase, Divider, Popover, Stack, Typography, useTheme } from '@mui/material';
import { useLocale } from '@repo/i18n';
import { NativeActiveMemberCard } from '@repo/types';
import { MountPointContext } from '@repo/common-utils/mountPoint';

type MemberCardLinePickerProps = {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    cards: NativeActiveMemberCard[];
    selectedCardId: string | null;
    onPick: (cardId: string) => void;
    onUseAuto: () => void;
};

// Per-line manual card picker — lists the cards eligible for this line + an "automatic" reset.
export default function MemberCardLinePicker({
    anchorEl,
    open,
    onClose,
    cards,
    selectedCardId,
    onPick,
    onUseAuto,
}: MemberCardLinePickerProps): JSX.Element {
    const { t } = useLocale();
    const theme = useTheme();
    const mountPoint = useContext(MountPointContext);
    const radius = `${theme.shape.borderRadius}px`;

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            container={mountPoint.popover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
            <Stack spacing={0.5} sx={{ p: 1, minWidth: 240 }}>
                {cards.map((card) => {
                    const selected = card.id === selectedCardId;
                    return (
                        <ButtonBase
                            key={card.id}
                            onClick={() => {
                                onPick(card.id);
                                onClose();
                            }}
                            aria-pressed={selected}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 1,
                                width: '100%',
                                minHeight: 44,
                                px: 1.5,
                                borderRadius: radius,
                                textAlign: 'left',
                                bgcolor: selected ? theme.palette.action.selected : 'transparent',
                            }}
                        >
                            <Typography variant="body2" fontWeight={600} noWrap>
                                {card.product_name}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                {card.flavor === 'punch'
                                    ? `${card.punches_remaining ?? 0}`
                                    : `${card.discount_percentage ?? 0}%`}
                            </Typography>
                        </ButtonBase>
                    );
                })}
                <Divider />
                <ButtonBase
                    onClick={() => {
                        onUseAuto();
                        onClose();
                    }}
                    sx={{
                        justifyContent: 'flex-start',
                        width: '100%',
                        minHeight: 40,
                        px: 1.5,
                        borderRadius: radius,
                    }}
                >
                    <Typography variant="body2" color="primary">
                        {t.use_auto_selection}
                    </Typography>
                </ButtonBase>
            </Stack>
        </Popover>
    );
}
